/**
 * Panel shown to the right of the TV when execution is ongoing (lg+).
 * Shows: placed bets for the current event (from API), or "You have no bets for this event", or "Log in to place bets" CTA.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { openLoginModal } from 'mfe/auth-bridge';
import { isGuestUserSession, UserSessionContext } from 'features/session/userSessionStore';
import { useTicketsForEventQuery } from 'features/tickets/queries';
import ModernTipItem from 'features/tickets/components/ModernTipItem';
import { cn } from 'lib/utils';

/** Map API tip (RichTicketTip) to ModernTipItem props for read-only display */
function mapPlacedTipToProps(tip) {
  const marketName = tip.ticket_type === 'SYSTEM'
    ? `${tip.market_type_description ?? ''} · ${tip.ticket_type}`
    : tip.market_type_description;
  return {
    event_id: tip.event_id,
    outcome_id: tip.outcome_id,
    odd: tip.odd,
    sport_id: tip.sport_id ?? undefined,
    description: tip.event_description ?? undefined,
    market_name: marketName ?? undefined,
    outcome: tip.outcome_label ?? tip.outcome_description,
    runner_number: tip.outcome_label ?? tip.runner_number,
    n_runners: tip.n_runners,
    outcome_type_id: tip.outcome_type_id,
  };
}

export default function EventBetsPanel({ eventId }) {
  const { t } = useTranslation();
  const [{ user }] = React.useContext(UserSessionContext);
  const isGuest = isGuestUserSession(user);
  const isLoggedIn = user && !isGuest;

  const { data: tickets, isLoading, isError } = useTicketsForEventQuery(eventId, {
    enabled: isLoggedIn && eventId != null,
  });

  const placedTipsForEvent = React.useMemo(() => {
    if (eventId == null || !tickets) return [];
    return tickets
      .flatMap((t) =>
        (t.tips ?? []).map((tip) => ({
          ...tip,
          ticket_type: t.type,
        }))
      )
      .filter((tip) => String(tip.event_id) === String(eventId));
  }, [tickets, eventId]);

  const handleOpenLogin = () => {
    openLoginModal();
  };

  return (
    <div
      className={cn(
        'event-bets-panel w-full flex flex-col min-h-0',
        'bg-card overflow-hidden'
      )}
    >
      <div className="px-3 py-2 border-b border-border bg-muted/50 font-semibold text-sm text-foreground shrink-0">
        {t('YOUR_BETS_FOR_THIS_EVENT') || 'Your bets for this event'}
      </div>
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto p-2 justify-center">
        {!isLoggedIn && (
          <div className="py-4 px-3 text-center text-sm text-muted-foreground">
            <p className="mb-3">{t('LOG_IN_TO_PLACE_BETS') || 'Log in to place bets.'}</p>
            <button
              type="button"
              onClick={handleOpenLogin}
              className="px-3 py-1.5 rounded-md text-xs font-medium c7-sport-bg c7-sport-bg-hover focus:outline-none c7-sport-ring focus:ring-offset-2"
            >
              {t('LOG_IN') || 'Log in'}
            </button>
          </div>
        )}
        {isLoggedIn && isLoading && (
          <div className="py-4 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-border border-t-foreground/60 rounded-full animate-spin" />
          </div>
        )}
        {isLoggedIn && isError && (
          <div className="py-4 px-3 text-center text-sm text-muted-foreground">
            {t('ERROR_LOADING_BETS', 'Could not load your bets.')}
          </div>
        )}
        {isLoggedIn && !isLoading && !isError && placedTipsForEvent.length === 0 && (
          <div className="py-4 px-3 text-center text-sm text-muted-foreground">
            {t('YOU_HAVE_NO_BETS_FOR_THIS_EVENT') || 'You have no bets for this event.'}
          </div>
        )}
        {isLoggedIn && !isLoading && !isError && placedTipsForEvent.length > 0 && (
          <div className="space-y-0 divide-y divide-border">
            {placedTipsForEvent
              .filter((tip) => tip.outcome_id != null)
              .map((tip) => (
                <ModernTipItem
                  {...mapPlacedTipToProps(tip)}
                  readOnly
                  key={tip.tip_id ?? `${tip.ticket_id}-${tip.outcome_id}`}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
